import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatMonedaCompacta, formatFecha } from '../state/format';
import { centroApi } from '../api/centroApi';
import { CentroApiError, BolsaCentro } from '../types';
import { useToast } from './Toast';
import { BlockingOverlay } from '../../../../../shared/components/BlockingOverlay';

interface BagStripProps {
  bolsasAbiertas: BolsaCentro[];
  onConvertida: (bolsaId: string) => void;
}

export const BagStrip: React.FC<BagStripProps> = ({ bolsasAbiertas, onConvertida }) => {
  const { showToast } = useToast();
  const [convirtiendo, setConvirtiendo] = useState<string | null>(null);

  const handleConvertir = async (id: string) => {
    setConvirtiendo(id);
    try {
      const bolsa = await centroApi.convertirBolsa(id);
      showToast(`${bolsa.codigo_cotizacion ?? bolsa.id} → BORRADOR`);
      onConvertida(id);
    } catch (error) {
      const message = error instanceof CentroApiError ? error.message : 'No se pudo convertir la bolsa';
      showToast(message, 'error');
    } finally {
      setConvirtiendo(null);
    }
  };

  return (
    <Box>
      <BlockingOverlay open={convirtiendo !== null} />
      {/* SPEC-105 (CH-01) — título ahora lo provee el SectionCard que envuelve este
          componente en PipelineTab; se elimina el duplicado. */}
      {bolsasAbiertas.length === 0 ? (
        <Box
          sx={{
            border: `1.5px dashed ${TOKENS.faint}`,
            borderRadius: '14px',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), color: TOKENS.faint }}>
            No hay bolsas abiertas — crea una desde el catálogo para empezar a mercar.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: '14px', overflowX: 'auto', paddingBottom: '6px' }}>
          {bolsasAbiertas.map((b) => (
            <Box
              key={b.id}
              data-testid="bag-card"
              sx={{
                minWidth: 260,
                backgroundColor: TOKENS.wash,
                border: `1.5px dashed ${TOKENS.purpleSoft}`,
                borderRadius: '14px',
                padding: '14px 16px',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,.6), 0 2px 4px rgba(46,36,48,.05)',
                transition: 'all .15s ease',
                '&:hover': { backgroundColor: '#fff', boxShadow: '0 4px 8px rgba(46,36,48,.09), 0 16px 34px rgba(46,36,48,.17)', transform: 'translateY(-2px)' },
              }}
            >
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 600, color: TOKENS.ink }}>
                {b.codigo_bolsa ?? b.id}
              </Typography>
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), color: TOKENS.muted, marginBottom: '10px' }}>
                {b.cliente_razon_social ?? 'Sin cliente'} · {b.lineas_count} referencias · ~{formatMonedaCompacta(b.total)} · desde {formatFecha(b.createdAt)}
              </Typography>
              <Button
                data-testid="bag-convertir"
                disabled={convirtiendo === b.id || b.lineas_count === 0}
                onClick={() => handleConvertir(b.id)}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: fs(12),
                  fontWeight: 600,
                  backgroundColor: TOKENS.purple,
                  color: '#fff',
                  borderRadius: '999px',
                  padding: '5px 14px',
                  '&:hover': { backgroundColor: TOKENS.purpleDark },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.7)' },
                }}
              >
                Convertir a cotización →
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
